import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate as formatDateUtil, formatTimeString, formatDateForInput } from '../../utils/dateUtils';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  SportsTennis as SportsTennisIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  BookOnline as BookOnlineIcon,
  AccessTime as AccessTimeIcon,
  People as PeopleIcon,
  Event as EventIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { groupPlayAPI, memberProfileAPI } from '../../services/api';
import { GroupPlay, GroupPlayBooking, MemberProfile, TierAccessLevel, GroupPlayInstance, GroupPlayStatus, GroupPlayBookingStatus } from '../../types';

// Extended GroupPlay interface to include user booking status
interface GroupPlayWithBookingStatus extends GroupPlay {
  userBooking?: GroupPlayBooking;
  isUserBooked: boolean;
  tierAccessLevel?: TierAccessLevel;
  isAccessible: boolean;
  currentBookings?: number;
  nextAvailableInstance?: GroupPlayInstance;
}

const MemberGroupPlaysPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for group plays and filters
  const [groupPlays, setGroupPlays] = useState<GroupPlayWithBookingStatus[]>([]);
  const [filteredGroupPlays, setFilteredGroupPlays] = useState<GroupPlayWithBookingStatus[]>([]);
  const [groupPlayTypes, setGroupPlayTypes] = useState<string[]>([]);
  const [userBookings, setUserBookings] = useState<GroupPlayBooking[]>([]);
  const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch member profile first to get tier information
        const selectedProfileData = localStorage.getItem('selectedMemberProfile');
        let profile: MemberProfile | null = null;
        
        if (selectedProfileData) {
          try {
            const selectedProfile = JSON.parse(selectedProfileData);
            const selectedProfileId = selectedProfile.id;
            const profileResponse = await memberProfileAPI.getMemberProfileById(selectedProfileId);
            profile = profileResponse.data;
            setMemberProfile(profile);
          } catch (err) {
            console.error('Error fetching member profile:', err);
          }
        }
        
        // Fetch all data in parallel
        const [groupPlaysResponse, typesResponse, bookingsResponse] = await Promise.all([
          groupPlayAPI.getGroupPlays(),
          groupPlayAPI.getGroupPlayTypes(),
          groupPlayAPI.getUserBookings()
        ]);

        // Process group plays - filter for active
        // For recurring group plays, don't filter by parent scheduleDate since instances may be in the future
        const activeGroupPlays = groupPlaysResponse.data.filter((groupPlay: GroupPlay) => {
          // Only filter by active status and status
          return groupPlay.active && (groupPlay.status === GroupPlayStatus.ACTIVE || groupPlay.status === GroupPlayStatus.SCHEDULED);
        });

        // Set types
        setGroupPlayTypes(typesResponse.data || []);
        setUserBookings(bookingsResponse.data);

        // Merge group plays with bookings and tier access
        const groupPlaysWithBookings = await mergeGroupPlaysWithBookings(activeGroupPlays, bookingsResponse.data, profile);
        setGroupPlays(groupPlaysWithBookings);

      } catch (error: any) {
        console.error('Error fetching initial data:', error);
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Filter group plays when search term or filters change
  useEffect(() => {
    filterGroupPlays();
  }, [groupPlays, searchTerm, selectedType, selectedDate, selectedDayOfWeek]);

  const mergeGroupPlaysWithBookings = async (
    groupPlaysData: GroupPlay[], 
    bookingsData: GroupPlayBooking[], 
    profile: MemberProfile | null
  ): Promise<GroupPlayWithBookingStatus[]> => {
    console.log('Merging group plays with bookings:', {
      groupPlaysCount: groupPlaysData.length,
      bookingsCount: bookingsData.length,
      membershipTier: profile?.membershipTier?.name
    });

    // Fetch all instances over the next 3 months in one call (API allows at most 92 days inclusive)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(today);
    endDate.setMonth(endDate.getMonth() + 3);
    const maxEndDate = new Date(today);
    maxEndDate.setDate(maxEndDate.getDate() + 91);
    if (endDate > maxEndDate) {
      endDate.setTime(maxEndDate.getTime());
    }

    // Group the available (scheduled/active) instances by their parent group play
    const instancesByGroupPlay = new Map<string, GroupPlayInstance[]>();
    try {
      const scheduleResponse = await groupPlayAPI.getGroupPlaySchedule({
        startDate: formatDateForInput(today),
        endDate: formatDateForInput(endDate)
      });

      (scheduleResponse.data || [])
        .filter((instance: GroupPlayInstance) =>
          instance.status === 'SCHEDULED' || instance.status === 'ACTIVE'
        )
        .forEach((instance: GroupPlayInstance) => {
          const list = instancesByGroupPlay.get(instance.parentGroupPlayId) || [];
          list.push(instance);
          instancesByGroupPlay.set(instance.parentGroupPlayId, list);
        });
    } catch (error) {
      console.error('Error fetching group play schedule:', error);
    }

    const groupPlaysWithInstances = await Promise.all(
      groupPlaysData.map(async (groupPlay) => {
        // Pick the earliest available instance for this group play
        let nextInstance: GroupPlayInstance | undefined;
        const availableInstances = [...(instancesByGroupPlay.get(groupPlay.id) || [])]
          .sort((a: GroupPlayInstance, b: GroupPlayInstance) => {
            const dateA = new Date(`${a.sessionDate}T${a.startTime.split('+')[0].split('-')[0]}`);
            const dateB = new Date(`${b.sessionDate}T${b.startTime.split('+')[0].split('-')[0]}`);
            return dateA.getTime() - dateB.getTime();
          });

        if (availableInstances.length > 0) {
          nextInstance = availableInstances[0];
        }

        // Check if user has any bookings for this group play's instances
        const userBooking = bookingsData.find(booking => 
          booking.groupPlayInstance?.parentGroupPlayId === groupPlay.id && 
          booking.status !== GroupPlayBookingStatus.CANCELLED
        );
        
        const isUserBooked = !!userBooking;
        
        // Determine tier access level for this group play
        const tierName = profile?.membershipTier?.name;
        let tierAccessLevel: TierAccessLevel = TierAccessLevel.FREE;
        let isAccessible = true;
        
        if (tierName && groupPlay.tierAccess) {
          const configuredAccess = groupPlay.tierAccess[tierName];
          if (configuredAccess) {
            tierAccessLevel = configuredAccess as TierAccessLevel;
            isAccessible = tierAccessLevel !== TierAccessLevel.NO_ACCESS;
          }
        }
        
        return {
          ...groupPlay,
          userBooking,
          isUserBooked,
          tierAccessLevel,
          isAccessible,
          currentBookings: nextInstance?.confirmedCount || 0,
          nextAvailableInstance: nextInstance
        };
      })
    );

    // Filter out group plays with no available instances (but keep full ones)
    return groupPlaysWithInstances.filter(gp => gp.nextAvailableInstance);
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      
      // Fetch group plays and bookings
      const [groupPlaysResponse, bookingsResponse] = await Promise.all([
        groupPlayAPI.getGroupPlays(),
        groupPlayAPI.getUserBookings()
      ]);

      // Process group plays - filter for active
      // For recurring group plays, don't filter by parent scheduleDate since instances may be in the future
      const activeGroupPlays = groupPlaysResponse.data.filter((groupPlay: GroupPlay) => {
        // Only filter by active status and status
        return groupPlay.active && (groupPlay.status === GroupPlayStatus.ACTIVE || groupPlay.status === GroupPlayStatus.SCHEDULED);
      });
      
      setUserBookings(bookingsResponse.data);

      // Merge group plays with bookings and tier access
      const groupPlaysWithBookings = await mergeGroupPlaysWithBookings(activeGroupPlays, bookingsResponse.data, memberProfile);
      setGroupPlays(groupPlaysWithBookings);

    } catch (error: any) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const filterGroupPlays = () => {
    let filtered = groupPlays;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((groupPlay: GroupPlayWithBookingStatus) =>
        groupPlay.name.toLowerCase().includes(searchLower) ||
        (groupPlay.coachName || groupPlay.guestCoachName || '').toLowerCase().includes(searchLower) ||
        groupPlay.description?.toLowerCase().includes(searchLower) ||
        groupPlay.type?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter((groupPlay: GroupPlayWithBookingStatus) => groupPlay.type === selectedType);
    }

    // Filter by date
    if (selectedDate) {
      // Format date without timezone conversion
      const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      filtered = filtered.filter((groupPlay: GroupPlayWithBookingStatus) => {
        const sessionDate = groupPlay.nextAvailableInstance?.sessionDate || groupPlay.scheduleDate;
        return sessionDate === selectedDateStr;
      });
    }

    // Filter by day of week
    if (selectedDayOfWeek) {
      filtered = filtered.filter((groupPlay: GroupPlayWithBookingStatus) => {
        const sessionDate = groupPlay.nextAvailableInstance?.sessionDate || groupPlay.scheduleDate;
        // Parse date as YYYY-MM-DD without timezone conversion
        const [year, month, day] = sessionDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        return dayOfWeek.toString() === selectedDayOfWeek;
      });
    }

    // Sort by date and time
    filtered.sort((a, b) => {
      const dateA = a.nextAvailableInstance?.sessionDate || a.scheduleDate;
      const dateB = b.nextAvailableInstance?.sessionDate || b.scheduleDate;
      
      const timeOnlyA = (a.nextAvailableInstance?.startTime || a.startTime).split('+')[0].split('-')[0];
      const timeOnlyB = (b.nextAvailableInstance?.startTime || b.startTime).split('+')[0].split('-')[0];
      
      const fullDateA = new Date(`${dateA}T${timeOnlyA}`);
      const fullDateB = new Date(`${dateB}T${timeOnlyB}`);
      return fullDateA.getTime() - fullDateB.getTime();
    });

    setFilteredGroupPlays(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('');
    setSelectedDate(null);
    setSelectedDayOfWeek('');
  };

  const handleViewGroupPlay = async (groupPlay: GroupPlayWithBookingStatus) => {
    // Navigate directly to the next available instance
    if (groupPlay.nextAvailableInstance) {
      navigate(`/member/group-play-booking/${groupPlay.id}/${groupPlay.nextAvailableInstance.id}`);
    } else {
      setError('No available sessions found for this group play');
    }
  };

  const formatTime = (timeString: string) => {
    return formatTimeString(timeString);
  };

  const formatRecurringPattern = (groupPlay: GroupPlay) => {
    if (!groupPlay.recurringPattern || !groupPlay.recurringPattern.daysOfWeek || groupPlay.recurringPattern.daysOfWeek.length === 0) {
      return null;
    }
    
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const days = groupPlay.recurringPattern.daysOfWeek.map((day: number) => daysOfWeek[day]).join(', ');
    
    return `Occurs every week on ${days}`;
  };

  const getAvailabilityStatus = (groupPlay: GroupPlayWithBookingStatus) => {
    const available = groupPlay.capacity - (groupPlay.currentBookings || 0);
    const percentageAvailable = (available / groupPlay.capacity) * 100;
    
    if (available <= 0) {
      if (groupPlay.isWaitlistAvailable) {
        return { text: 'Waitlist', color: 'warning' as const };
      }
      return { text: 'Full', color: 'error' as const };
    }
    
    if (percentageAvailable > 50) {
      return { text: 'Available', color: 'success' as const };
    }
    
    return { text: 'Limited', color: 'warning' as const };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
          <SportsTennisIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Group Play Sessions
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Join organized group play sessions
        </Typography>
      </Paper>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: isMobile ? 2 : 3 }}>
          <Stack spacing={2}>
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search group play sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              size={isMobile ? 'small' : 'medium'}
            />

            {/* Filter Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? 'contained' : 'outlined'}
                size="small"
              >
                Filters
              </Button>
              
              {(selectedType || selectedDate || selectedDayOfWeek) && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                  color="secondary"
                >
                  Clear
                </Button>
              )}

              {/* Active Filter Tags */}
              {selectedType && (
                <Chip 
                  label={`Type: ${selectedType}`} 
                  onDelete={() => setSelectedType('')}
                  size="small"
                />
              )}
              {selectedDate && (
                <Chip 
                  label={`Date: ${formatDateUtil(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`)}`} 
                  onDelete={() => setSelectedDate(null)}
                  size="small"
                />
              )}
              {selectedDayOfWeek && (
                <Chip 
                  label={`Day: ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][parseInt(selectedDayOfWeek)]}`} 
                  onDelete={() => setSelectedDayOfWeek('')}
                  size="small"
                />
              )}
            </Box>

            {/* Expanded Filters */}
            {showFilters && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Activity Type</InputLabel>
                    <Select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      label="Activity Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      {groupPlayTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Day of Week</InputLabel>
                    <Select
                      value={selectedDayOfWeek}
                      onChange={(e) => setSelectedDayOfWeek(e.target.value)}
                      label="Day of Week"
                    >
                      <MenuItem value="">All Days</MenuItem>
                      <MenuItem value="1">Monday</MenuItem>
                      <MenuItem value="2">Tuesday</MenuItem>
                      <MenuItem value="3">Wednesday</MenuItem>
                      <MenuItem value="4">Thursday</MenuItem>
                      <MenuItem value="5">Friday</MenuItem>
                      <MenuItem value="6">Saturday</MenuItem>
                      <MenuItem value="0">Sunday</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Session Date"
                      value={selectedDate}
                      onChange={(newDate) => setSelectedDate(newDate)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          placeholder: 'Select a date'
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Group Plays Grid */}
      {filteredGroupPlays.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No group play sessions found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || selectedType
                ? 'Try adjusting your filters to see more sessions'
                : 'Check back later for new group play sessions'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredGroupPlays.map((groupPlay) => {
            const availability = getAvailabilityStatus(groupPlay);
            const isNoAccess = !groupPlay.isAccessible;
            const coachName = groupPlay.isGuestCoach ? groupPlay.guestCoachName : groupPlay.coachName;
            
            return (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={groupPlay.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    opacity: isNoAccess ? 0.6 : 1,
                    backgroundColor: isNoAccess ? 'action.disabledBackground' : 'background.paper',
                    '&:hover': {
                      transform: isNoAccess ? 'none' : 'translateY(-2px)',
                      boxShadow: isNoAccess ? 'none' : theme.shadows[4]
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        {groupPlay.name}
                      </Typography>
                      {groupPlay.isUserBooked && (
                        <Chip
                          label="Registered"
                          color="success"
                          size="small"
                          variant="outlined"
                          icon={<CheckCircleIcon />}
                        />
                      )}
                    </Box>
                    
                    {/* Group Play Details */}
                    <Stack spacing={1.5}>
                      {groupPlay.type && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SportsTennisIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {groupPlay.type}
                          </Typography>
                        </Box>
                      )}
                      
                      {coachName && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {coachName}
                          </Typography>
                        </Box>
                      )}
                      
                      {groupPlay.nextAvailableInstance?.venueNames && groupPlay.nextAvailableInstance.venueNames.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationOnIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {groupPlay.nextAvailableInstance.venueNames.join(', ')}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                          {formatDateUtil(groupPlay.nextAvailableInstance?.sessionDate || groupPlay.scheduleDate)}
                        </Typography>
                      </Box>
                      
                      {formatRecurringPattern(groupPlay) && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <RepeatIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
                            {formatRecurringPattern(groupPlay)}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(groupPlay.nextAvailableInstance?.startTime || groupPlay.startTime)} - {formatTime(groupPlay.nextAvailableInstance?.endTime || groupPlay.endTime)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Capacity: {groupPlay.nextAvailableInstance?.capacity || groupPlay.capacity}
                        </Typography>
                      </Box>
                      
                      {groupPlay.tierAccessLevel === TierAccessLevel.PAID && groupPlay.memberSessionFee > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                            ${groupPlay.nextAvailableInstance?.memberFeeOverride ?? groupPlay.memberSessionFee} per session
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium' }}>
                            Free
                          </Typography>
                        </Box>
                      )}
                      
                      {(groupPlay.nextAvailableInstance?.description || groupPlay.description) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                          {groupPlay.nextAvailableInstance?.description || groupPlay.description}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions sx={{ p: 2 }}>
                    {isNoAccess ? (
                      <Button
                        variant="outlined"
                        disabled
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                        sx={{ 
                          color: 'text.disabled',
                          borderColor: 'action.disabled'
                        }}
                      >
                        Not Accessible
                      </Button>
                    ) : (() => {
                      const instance = groupPlay.nextAvailableInstance;
                      const confirmedCount = instance?.confirmedCount || 0;
                      const capacity = instance?.capacity || groupPlay.capacity;
                      const isFull = confirmedCount >= capacity;
                      const hasWaitlist = instance?.isWaitlistAvailable || groupPlay.isWaitlistAvailable;

                      if (isFull && !hasWaitlist) {
                        return (
                          <Button
                            variant="outlined"
                            disabled
                            fullWidth
                            size={isMobile ? 'small' : 'medium'}
                            sx={{ 
                              color: 'text.disabled',
                              borderColor: 'action.disabled'
                            }}
                          >
                            Full
                          </Button>
                        );
                      }

                      if (isFull && hasWaitlist) {
                        return (
                          <Button
                            variant="contained"
                            color="warning"
                            startIcon={<PeopleIcon />}
                            onClick={() => handleViewGroupPlay(groupPlay)}
                            fullWidth
                            size={isMobile ? 'small' : 'medium'}
                          >
                            Join Waitlist
                          </Button>
                        );
                      }

                      return (
                        <Button
                          variant="contained"
                          startIcon={<BookOnlineIcon />}
                          onClick={() => handleViewGroupPlay(groupPlay)}
                          fullWidth
                          size={isMobile ? 'small' : 'medium'}
                        >
                          Book Session
                        </Button>
                      );
                    })()}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default MemberGroupPlaysPage;
